import * as Actions from '../actions/taskActions';
import Task from '../../entities/task';
import * as idb from '../../lib/idbd';
import locals from '../../lib/localStorage';
import ArrayMap from '../../lib/ArrayMap';
import ArrayMapRegistry from '../../lib/ArrayMapRegistry';
import {ensure} from '../../lib/picos/ensure';
import {isRecoverableError} from '../../lib/recoverableErrors';

export type TaskAction = { 
    type: string; 
    task: Task | null; 
    taskId: string | Task; 
    partial: boolean; 
    lastFetch: number; 
    tasks: Task[]; 
    err: any; 
    status: number; 
    message: string; 
}

let logging: boolean = locals.getBoolean(locals.REDUX_LOGGING, false);

/**
 * Call the storage worker.
 *
 * @param {ArrayMap<Task> | null} data
 * @param {string} firmId
 * @param {string} userId
 * @param {Task[]} [updates]
 */
const callStorageWorker = (data: ArrayMap<Task> | null, firmId: string, userId: string, updates?: Task[]) => {
	window.storageWorker
		  .updateStorage(firmId, userId, 'task', 'tasks', data?.values() || null, updates)
		  .catch(console.error);
}

/**
 * Update storage in backend.
 *
 * @param {ArrayMap<Task>} data
 * @param {Task[]} [updates]
 * @returns {ArrayMap<Task>}
 */
function updateStorage(data: ArrayMap<Task>, updates?: Task[]): ArrayMap<Task> {
	if (logging) console.debug('⮂ taskReducer: updateStorage');
	
	const firmId = locals.getString(locals.USER_FIRM_ID, '');
	const userId = locals.getString(locals.USER_ID, '');

	if (updates && updates.length === 0) return data;
	else if (updates) callStorageWorker(null, firmId, userId, updates);
	else callStorageWorker(data, firmId, userId);

	return data;
}

/**
 * Update storage in backend.
 *
 * @param {Task[]} [updates]
 * @returns {ArrayMap<Task>}
 */
function updateStorageNoReturn(updates?: Task[]): void {
	if (logging) console.debug('⮂ taskReducer: updateStorageNoReturn');
	
	const firmId = locals.getString(locals.USER_FIRM_ID, '');
	const userId = locals.getString(locals.USER_ID, '');

	if (updates && updates.length > 0) callStorageWorker(null, firmId, userId, updates);
}

/**
 * Create state with optional data.
 *
 * @param {Task[]} [data]
 * @returns {ArrayMap<Task>}
 */
function createState(data?: Task[] | ArrayMap<Task>): ArrayMap<Task> {
	if (!data) return ArrayMapRegistry.clone<Task>('task', []);
    else if (data instanceof ArrayMap) return ArrayMapRegistry.clone<Task>('task', data.map(e => e));
    else return ArrayMapRegistry.clone<Task>('task', data);
}

function tasks(state: ArrayMap<Task>, action: TaskAction): ArrayMap<Task> {
	if (!state) state = createState();

	let st: ArrayMap<Task>;

	switch (action.type) {
		// action.task - the new value of task with id action.id
		// action.taskId - id of the target, used if new value is null
		case Actions.TASK_FETCH_ONE_SUCCESS:
			/* TODO: This is busted for all but modifications! */
			if (logging) console.debug('⮂ taskReducer: FETCH_ONE_SUCCESS', action);

			if (action.task === null) return state;

			// The addedOn date of the existing one will always be greater than the one passed through
			// (Because it was updated!)
			if (state.has(action.taskId)) {
				st = state.clone();
				st.set(action.task);
				// state.clear();

				updateStorageNoReturn([action.task]);
				return st;
			}
			else return state;

		/* Handle incoming fetch:
			 partial, stateEmpty, fetchEmpty		-> A do nothing, return state
			 partial, stateEmpty, !fetchEmpty	 -> B return action.tasks
			 partial, !stateEmpty, fetchEmpty	 -> A do nothing, return state
			 partial, !stateEmpty, !fetchEmpty	-> C update/add entries in action.tasks
			 !partial, stateEmpty, fetchEmpty	 -> A do nothing, return state
			 !partial, stateEmpty, !fetchEmpty	-> B return action.tasks
			 !partial, !stateEmpty, fetchEmpty	-> D return tasks in state newer than last fetch time
			 !partial, !stateEmpty, !fetchEmpty -> E merge tasks in state newer than those in action.tasks
		 */
		case Actions.TASK_FETCH_SUCCESS:
			const partial = action.partial;
			const lastFetch = action.lastFetch;
			const stateEmpty = state.length === 0;
			const fetchEmpty = action.tasks.length === 0;
			const fetched = action.tasks.map(task => new Task({...task, persisted: true}));

			if (logging) console.debug(`⮂ taskReducer: ${partial ? 'partial' : 'full'} fetch of ${fetched.length} tasks with ${stateEmpty ? 'empty' : 'non-empty'} state and ${fetchEmpty ? 'empty' : 'non-empty'} fetch`);

			// ✓ We didn't have anything in our state, so whatever is in fetched is the new state
			const replaceState = () => {
				if (logging) console.debug('⮂ taskReducer: replaceState', {partial, stateEmpty, fetchEmpty, lastFetch});

				if (fetched.length) {
					// Create a new state based on the fetched results.
					st = createState(fetched);

					return updateStorage(st);
				}
				else return state;
			}

			// ✓ Partial fetch with result and state was not empty, update state with fetched result.
			//	 Bear in mind that partial fetches includes deletes for all entities, so we need to use that
			//	 information to delete from current state if status of the fetched result is 'D'.
			const partialMerge = () => {
				if (logging) console.debug('⮂ taskReducer: partialMerge', {partial, stateEmpty, fetchEmpty, lastFetch});

				// Use a fresh copy of the state
				const st: ArrayMap<Task> = state.clone();

				// We only updateStorage if there is something to update
				const toUpdate: Task[] = [];

				// For each item, add it if it doesn't exist, replace if it does, and check for deleted
				fetched.forEach(task => {
					const current = st.get(task.id);

					if (current) {
						/* Has it been deleted?
						 -- Note that partials include deleted for everything!
						 -- Note that some tables retain the deleted, and some do not.
						 -- We need to do the right thing here
						*/
						if (current.status !== 'D' && task.status === 'D') {
							// Remove it from state
							toUpdate.push(task);
							st.delete(task);
						}

						/* Not deleted */
						else {
							// Is it newer than what we have? Update the backups and set it locally
							if (task.addedOn > current.addedOn) {
								toUpdate.push(task);
								st.set(task);
							}
						}
					}

					// Otherwise add it (but not if it's deleted — no point adding a deleted entry we never had)
					else {
						if (task.status !== 'D') {
							toUpdate.push(task);
							st.set(task);
						}
						else if (logging) console.debug('⮂ taskReducer: partialMerge: skipping deleted task not in state');
					}
				});

				return updateStorage(st, toUpdate);
			};

			// ✓ Full dataset with no data returned but existing state: return empty set.
			const empty = () => {
				if (logging) console.debug('⮂ taskReducer: empty: tasks', {partial, stateEmpty, fetchEmpty, lastFetch});

				// Clone and clear to produce an empty state with the same schema
				st = state.clone();
				st.clear();

				return updateStorage(st);
			};

			// ✓ Full dataset returned with entries but existing state, must merge.
			//	 The lastFetch variable will have the time (hrt) we started the fetch, which we can use to determine
			//	 which to use, state or fetched.
			//	 For each fetched result, we need to find the matching entry in state.
			//	 Then we need to double-check that the state has not been updated since the last save.
			const fullMerge = () => {
				if (logging) console.debug('⮂ taskReducer: fullMerge: tasks', {partial, stateEmpty, fetchEmpty, lastFetch});

				st = createState(fetched); // Start with the new stuff

				// Fetched represents the entire dataset, so we just need to update the fetched results with anything not yet saved.
                state.forEach(localTask => {
                    // If this task has not yet been saved, the remote state may not know about it.
                    // BUT the persisted flag is unreliable (batch saves stamp it false), so a stale
                    // !persisted local must NOT shadow a newer authoritative server row (OWA-7 #1).
                    // Only re-apply a !persisted local when it is absent from the fetched set OR is
                    // strictly newer by addedOn than the fetched row for the same id. Otherwise the
                    // fetched (server) row wins.
                    if (!localTask.persisted) {
                        const fetchedTask = st.get(localTask.id);
                        if (!fetchedTask || localTask.addedOn > fetchedTask.addedOn) st.set(localTask);
                    }
                });

				return updateStorage(st);
			};

			if (partial) {										// ✓ Fetched results are only things changed since last check
				if (stateEmpty) {								// ✓ Partial fetch with nothing in state
					if (fetchEmpty) return state; 	// ✓ Partial fetch with no results and nothing in state just return state
					else return replaceState();			// ✓ Partial fetch with some results, but nothing in state, so this is the new state
				}
				else {											// ✓ There are existing entries in state
					if (fetchEmpty) return state;				// ✓ Partial fetch with no results, nothing to do but return state
					else return partialMerge(); 	// ✓ Partial fetch with result and state was not empty, update state with fetched result
				}
			}
			else {												// ✓ Full dataset returned
				if (stateEmpty) {								// ✓ We are getting back the full result set
					if (fetchEmpty) return state; 	// ✓ Full dataset with no data, but state was empty anyway, just return state
					else return replaceState();			// ✓ Full dataset returned with data, so this is new state
				}
				else {											// ✓ Full dataset returned but there is some data in state
					if (fetchEmpty) return empty();				// ✓ Full dataset with no data returned but existing state: return empty set.
					else return fullMerge();		// ✓ Full dataset returned with entries but existing state, must merge
				}
			}


		case Actions.TASK_FETCH_ONE_FAILURE:
			console.error('TASK_FETCH_ONE_FAILURE', action.taskId, action.err);

			// No update to storage required
			return state;

		case Actions.TASK_FETCH_FAILURE:
			console.error('TASK_FETCH_FAILURE', action.taskId, action.err);

			// No update to storage required
			return state;

		case Actions.TASK_RESTORE: {
		    let task = new Task({...action.task});
            st = state.clone();

            const existing = st.get(task.id);

            if (existing) {
                // Only update if the incoming version is at least as new
                if (task.addedOn >= existing.addedOn) {
                    st.set(task);
                    return updateStorage(st, [task]);
                }
                else return state;
            }
            else {
                // Don't add deleted entries that aren't already in state
                if (task.status === 'D') return state;

                st.set(task);
                return updateStorage(st, [task]);
            }
        }
            
		case Actions.TASK_SAVE: {
		    // console.debug('⮂ TASK_SAVE reducer', action.task?.id);
		    
			// clone the task
			let task = new Task({...action.task});

			st = state.clone();
			// state.clear(); 
			
			
			// Add or update the new task in the state
			// If this is a create, add to the queue
			const _task = st.get(task.id);

			if (!_task) st.set(task);
			else {
				if (task.addedOn >= _task.addedOn) st.set(task);
			}

			return updateStorage(st, [task]);
		}

		case Actions.TASK_SAVE_MANY: {
		    if (logging) console.debug('⮂ TASK_SAVE_MANY reducer', action.tasks.map(task => task.id).join(','));
		    
			// clone the tasks
			let toProcess = action.tasks.map(task => new Task(task));

			st = state.clone();
			// state.clear();

			const updates: Task[] = [];

			toProcess.forEach(task => {
				// Add or update the new task in the state
				// If this is a create, add to the queue
				const existing = st.get(task.id);

				if (!existing) {
					// Don't add deleted entries that aren't already in state
					if (task.status !== 'D') {
						st.push(task);
						updates.push(task);
					}
					else if (logging) console.debug('⮂ TASK_SAVE_MANY: skipping deleted task not in state');
				}
				else {
					if (task.addedOn >= existing.addedOn) {
						st.set(task);
						updates.push(task);
					}
					else if (logging) console.debug('⮂ Skipping, existing task had newer date.')
				}
			});

			return updateStorage(st, updates);
		}

		case Actions.TASK_REMOVE: {
			// clone the task
			const task = new Task({...action.task});

			// Remove from UI
			st = state.clone();
			// state.clear();

			st.delete(task);

			return updateStorage(st, [task]);
		}

		case Actions.TASK_RESTORE_FAILURE:
		case Actions.TASK_SAVE_FAILURE: {
		    console.warn('TASK_SAVE_FAILURE reducer', action.task?.id);
		    if (!action.task?.id) {
				console.warn('TASK_SAVE_FAILURE reducer', action.err);
				return state;
		    }
			
			const task = ensure(Task, action.task);
			if (!task) {
				console.error('Could not find task after call to save');
				return state;
			}

			st = state.clone();

			const _taskRef = state.get(task.id);

			if (_taskRef && (_taskRef.addedOn === task.addedOn)) {
				// Create a new instance to avoid mutating the entity shared with old state
				const _task = new Task({..._taskRef});
				_task.fatalError = {
					status: action.status,
					message: action.message,
					recoverable: isRecoverableError(action.status, action.message)
				};
				_task.persisted = true;	// Not really persisted, but can't keep it blue

				// Update the queue with the new info, so that we can filter these out in the queues
				idb.add('task', _task).then(r => {
						if (r.isErr()) console.error(`⮂ taskReducer: failed to save task with fatalError info: ${r.unwrapErr().message}`);
						else console.debug('⮂ taskReducer: saved task with fatalError info');
					}).catch(err => console.error(`⮂ taskReducer: failed to save task with fatalError info: ${err.message}`));

				st.set(_task);
			}

			return updateStorage(st);
		}

		case Actions.TASK_SAVE_MANY_FAILURE: {
		    console.warn('TASK_SAVE_MANY_FAILURE reducer', action.tasks.map(task => task.id).join(','));
		    
			// NOT IMPLEMENTED
			return state;
		}

		case Actions.TASK_RESTORE_SUCCESS:
		case Actions.TASK_SAVE_SUCCESS: {
			if (!action.task?.id) {
				console.warn('TASK_SAVE_SUCCESS reducer');
                return state;
			}

			const taskRef = state.get(action.task?.id);
			if (!taskRef) {
				console.warn('Could not find task after call to save');
                return state;
			}

			st = state.clone();

			if (action.task.addedOn >= taskRef.addedOn) {
				st.set(action.task);
			}
			else {
				// Create a new instance to avoid mutating the entity shared with old state
				const task = new Task({...taskRef, persisted: true});
				st.set(task);
			}

			return updateStorage(st, [action.task]);
		}

		case Actions.TASK_SAVE_MANY_SUCCESS: {
		    // console.debug('⮂ TASK_SAVE_MANY_SUCCESS reducer', action.tasks.map(task => task.id).join(','));

			// We need to update all of the saved items, then push the items to updateStorage
			st = state.clone();

			const tasks: Task[] = [];

			action.tasks.forEach(e => {
				const taskRef = st.get(e.id);

                // If we can't find it, it was probably deleted
				if (!taskRef) console.warn('Could not find task after call to save');
				else if (e.addedOn >= taskRef.addedOn) {
				    st.set(e);
				    tasks.push(e);
                }
				else {
					// Create a new instance to avoid mutating the entity shared with old state
					const task = new Task({...taskRef, persisted: true});
					st.set(task);
					tasks.push(task);
				}
			});

			return updateStorage(st, tasks);
		}

		case Actions.TASK_REMOVE_FAILURE: {
		    console.warn('TASK_REMOVE_FAILURE action', action.task?.id);
		    
			// No storage update needed
			return state;
		}

		case Actions.TASK_REMOVE_SUCCESS: {
			// No storage update needed
			return state;
		}

		case Actions.TASK_RESTORE_MANY: {
			if (logging) console.debug('⮂ TASK_RESTORE_MANY reducer', action.tasks.map(task => task.id).join(','));

			const toProcess = action.tasks.map(task => new Task({...task}));
			st = state.clone();

			const updates: Task[] = [];

			toProcess.forEach(task => {
				const existing = st.get(task.id);

				if (existing) {
					// Only update if the incoming version is at least as new
					if (task.addedOn >= existing.addedOn) {
						st.set(task);
						updates.push(task);
					}
					else if (logging) console.debug('⮂ TASK_RESTORE_MANY: skipping older version of task');
				}
				else {
					// Don't add deleted entries that aren't already in state
					if (task.status !== 'D') {
						st.set(task);
						updates.push(task);
					}
					else if (logging) console.debug('⮂ TASK_RESTORE_MANY: skipping deleted task not in state');
				}
			});

			return updateStorage(st, updates);
		}

		case Actions.TASK_REMOVE_MANY: {
			if (logging) console.debug('⮂ TASK_REMOVE_MANY reducer', action.tasks.map(task => task.id).join(','));

			const toProcess = action.tasks.map(task => new Task({...task}));
			st = state.clone();

			toProcess.forEach(task => {
				st.delete(task);
			});

			return updateStorage(st, toProcess);
		}

		default:
			// No storage update needed
			return state;
	}
}

export default tasks;